import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeftIcon,
  UserCircleIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { H1, H2, H3, Body, Card, Button, Badge, StatisticsCard } from '../components/ui';
import { Spinner } from '../components/ui/Loading';
import { CreateObservationModal } from '../components/observations';
import { useModal } from '../stores/uiStore';
import { useChild } from '../hooks/api/useChildren';
import { useObservationsByChild } from '../hooks/api/useObservations';
import { useAssessments, useLearningAreas } from '../hooks/api/useAssessments';
import { formatDateShort } from '../utils/date';

const genderLabel = (gender: string): string => {
  switch (gender) {
    case 'M': return 'Male';
    case 'F': return 'Female';
    case 'O': return 'Other';
    default: return gender;
  }
};

const ChildProfile: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const createObservationModal = useModal('create-observation');
  const [activeTab, setActiveTab] = useState<'overview' | 'observations' | 'assessments'>('overview');

  // Fetch data from API
  const {
    data: child,
    isLoading: childLoading,
    error: childError,
  } = useChild(childId || '');

  const {
    data: observationsData,
    isLoading: observationsLoading,
  } = useObservationsByChild(childId || '');

  const {
    data: assessmentsData,
    isLoading: assessmentsLoading,
  } = useAssessments({ child_id: childId });

  const { data: learningAreas } = useLearningAreas();

  // Build a lookup map from learning area ID to name
  const learningAreaMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (learningAreas) {
      for (const area of learningAreas) {
        map[area.id] = area.name;
      }
    }
    return map;
  }, [learningAreas]);

  // API may return array directly or { items: [...] } depending on endpoint
  const observations = Array.isArray(observationsData) ? observationsData : (observationsData?.items ?? []);
  const assessments = Array.isArray(assessmentsData) ? assessmentsData : (assessmentsData?.items ?? []);

  // Computed stats
  const totalObservations = observations.length;
  const publishedObservations = observations.filter((o) => o.is_published).length;
  const totalAssessments = assessments.length;
  const publishedAssessments = assessments.filter((a) => a.is_published).length;

  // Recent observations (last 5 by observation_date)
  const recentObservations = useMemo(() => {
    return [...observations]
      .sort((a, b) => new Date(b.observation_date).getTime() - new Date(a.observation_date).getTime())
      .slice(0, 5);
  }, [observations]);

  // Assessments grouped by learning_area_id
  const assessmentsByArea = useMemo(() => {
    const grouped: Record<string, typeof assessments> = {};
    for (const assessment of assessments) {
      const key = assessment.learning_area_id;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(assessment);
    }
    return grouped;
  }, [assessments]);

  const handleCreateObservation = () => {
    createObservationModal.open({ childId: childId });
  };

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: UserCircleIcon },
    { id: 'observations' as const, label: 'Observations', icon: ClipboardDocumentListIcon },
    { id: 'assessments' as const, label: 'Assessments', icon: ChartBarIcon },
  ];

  // Loading state
  if (childLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <Body className="mt-4 text-gray-500 dark:text-gray-400">Loading child profile...</Body>
        </div>
      </div>
    );
  }

  // Error state
  if (childError || !child) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <H2 className="text-xl font-semibold mb-2">Child Not Found</H2>
          <Body className="mb-4">
            {childError instanceof Error ? childError.message : 'Could not load this child profile.'}
          </Body>
          <Link to="/children">
            <Button variant="primary">Back to Children</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            {/* Back Button */}
            <div className="mb-4">
              <Link to="/children">
                <Button variant="ghost" size="sm" className="flex items-center">
                  <ChevronLeftIcon className="w-4 h-4 mr-2" />
                  Back to Children
                </Button>
              </Link>
            </div>

            {/* Child Info */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center mb-4 md:mb-0">
                <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-full mr-4">
                  <UserCircleIcon className="w-8 h-8 text-primary-600" />
                </div>
                <div>
                  <H1 className="text-3xl font-bold">
                    {child.name} {child.surname}
                  </H1>
                  <div className="flex items-center space-x-4 mt-2">
                    <Body className="text-gray-600 dark:text-gray-400">
                      Age {child.age} &middot; {genderLabel(child.gender)}
                    </Body>
                    {child.is_active === false && (
                      <Badge variant="warning" size="sm">Inactive</Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  variant="primary"
                  onClick={handleCreateObservation}
                  className="flex items-center"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Observation
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatisticsCard
            title="Total Observations"
            value={observationsLoading ? '...' : totalObservations}
            icon={ClipboardDocumentListIcon}
            color="primary"
          />
          <StatisticsCard
            title="Published Observations"
            value={observationsLoading ? '...' : publishedObservations}
            icon={ClipboardDocumentListIcon}
            color="success"
          />
          <StatisticsCard
            title="Total Assessments"
            value={assessmentsLoading ? '...' : totalAssessments}
            icon={ChartBarIcon}
            color="secondary"
          />
          <StatisticsCard
            title="Published Assessments"
            value={assessmentsLoading ? '...' : publishedAssessments}
            icon={ChartBarIcon}
            color="info"
          />
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-2">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Recent Observations */}
                <Card className="p-6">
                  <H2 className="text-xl font-semibold mb-4">Recent Observations</H2>
                  {observationsLoading ? (
                    <div className="flex justify-center py-8">
                      <Spinner size="md" />
                    </div>
                  ) : recentObservations.length === 0 ? (
                    <Body className="text-gray-500 dark:text-gray-400 py-4">No observations recorded yet.</Body>
                  ) : (
                    <div className="space-y-4">
                      {recentObservations.map((observation) => (
                        <div key={observation.id} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-b-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Badge variant="info" size="sm">
                                {observation.category}
                              </Badge>
                              {observation.is_published && (
                                <Badge variant="success" size="sm">Published</Badge>
                              )}
                              {(observation as any).verified && (
                                <Badge variant="primary" size="sm">Verified</Badge>
                              )}
                            </div>
                            <Body className="text-sm text-gray-500 dark:text-gray-400">
                              {formatDateShort(observation.observation_date)}
                            </Body>
                          </div>
                          <Body className="text-gray-700 dark:text-gray-300">
                            {observation.narrative}
                          </Body>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                {/* Assessment Summary */}
                <Card className="p-6">
                  <H2 className="text-xl font-semibold mb-4">Assessment Summary</H2>
                  {assessmentsLoading ? (
                    <div className="flex justify-center py-8">
                      <Spinner size="md" />
                    </div>
                  ) : Object.keys(assessmentsByArea).length === 0 ? (
                    <Body className="text-gray-500 dark:text-gray-400 py-4">No assessments recorded yet.</Body>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(assessmentsByArea).map(([areaId, areaAssessments]) => (
                        <div key={areaId} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                          <Body className="font-medium text-gray-700 dark:text-gray-300">
                            {learningAreaMap[areaId] || 'Learning Area'}
                          </Body>
                          <Badge variant="default" size="sm">
                            {areaAssessments.length} assessment{areaAssessments.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            )}

            {/* Observations Tab */}
            {activeTab === 'observations' && (
              <Card className="p-6">
                <H2 className="text-xl font-semibold mb-4">All Observations</H2>
                {observationsLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="md" />
                  </div>
                ) : observations.length === 0 ? (
                  <div className="text-center py-8">
                    <ClipboardDocumentListIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <Body className="text-gray-500 dark:text-gray-400 mb-4">No observations have been recorded for this child yet.</Body>
                    <Button variant="primary" size="sm" onClick={handleCreateObservation} className="flex items-center mx-auto">
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Add First Observation
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {[...observations]
                      .sort((a, b) => new Date(b.observation_date).getTime() - new Date(a.observation_date).getTime())
                      .map((observation) => (
                        <div key={observation.id} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-b-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Badge variant="info" size="sm">
                                {observation.category}
                              </Badge>
                              {observation.is_published ? (
                                <Badge variant="success" size="sm">Published</Badge>
                              ) : (
                                <Badge variant="warning" size="sm">Draft</Badge>
                              )}
                              {(observation as any).verified && (
                                <Badge variant="primary" size="sm">Verified</Badge>
                              )}
                            </div>
                            <Body className="text-sm text-gray-500 dark:text-gray-400">
                              {formatDateShort(observation.observation_date)}
                            </Body>
                          </div>
                          <Body className="text-gray-700 dark:text-gray-300">
                            {observation.narrative}
                          </Body>
                        </div>
                      ))}
                  </div>
                )}
              </Card>
            )}

            {/* Assessments Tab */}
            {activeTab === 'assessments' && (
              <Card className="p-6">
                <H2 className="text-xl font-semibold mb-4">Assessment History</H2>
                {assessmentsLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="md" />
                  </div>
                ) : assessments.length === 0 ? (
                  <div className="text-center py-8">
                    <ChartBarIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <Body className="text-gray-500 dark:text-gray-400">No assessments have been recorded for this child yet.</Body>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(assessmentsByArea).map(([areaId, areaAssessments]) => (
                      <div key={areaId}>
                        <H3 className="text-lg font-semibold mb-3">
                          {learningAreaMap[areaId] || 'Learning Area'}
                        </H3>
                        <div className="space-y-3">
                          {[...areaAssessments]
                            .sort((a, b) => new Date(b.assessment_date).getTime() - new Date(a.assessment_date).getTime())
                            .map((assessment) => (
                              <div key={assessment.id} className="border border-gray-100 dark:border-gray-700 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <Body className="text-sm text-gray-500 dark:text-gray-400">
                                    {formatDateShort(assessment.assessment_date)}
                                  </Body>
                                  <div className="flex items-center space-x-2">
                                    {assessment.score !== undefined && assessment.score !== null && (
                                      <Badge variant="primary" size="sm">
                                        Score: {assessment.score}
                                      </Badge>
                                    )}
                                    {assessment.is_published ? (
                                      <Badge variant="success" size="sm">Published</Badge>
                                    ) : (
                                      <Badge variant="warning" size="sm">Draft</Badge>
                                    )}
                                  </div>
                                </div>
                                {assessment.notes && (
                                  <Body className="text-gray-700 dark:text-gray-300 text-sm">{assessment.notes}</Body>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Child Details */}
            <Card className="p-6">
              <H3 className="text-lg font-semibold mb-4">Child Details</H3>
              <div className="space-y-3">
                <div>
                  <Body className="text-sm font-medium text-gray-700 dark:text-gray-300">Date of Birth</Body>
                  <Body className="text-sm text-gray-600 dark:text-gray-400">{formatDateShort(child.birthdate)}</Body>
                </div>
                <div>
                  <Body className="text-sm font-medium text-gray-700 dark:text-gray-300">Gender</Body>
                  <Body className="text-sm text-gray-600 dark:text-gray-400">{genderLabel(child.gender)}</Body>
                </div>
                <div>
                  <Body className="text-sm font-medium text-gray-700 dark:text-gray-300">Age</Body>
                  <Body className="text-sm text-gray-600 dark:text-gray-400">{child.age} years old</Body>
                </div>
                {child.classroom_id && (
                  <div>
                    <Body className="text-sm font-medium text-gray-700 dark:text-gray-300">Classroom</Body>
                    <Body className="text-sm text-gray-600 dark:text-gray-400">
                      {child.classroom
                        ? (typeof child.classroom === 'object' ? child.classroom.name : child.classroom)
                        : child.classroom_id}
                    </Body>
                  </div>
                )}
              </div>
            </Card>

            {/* Quick Actions */}
            <Card className="p-6">
              <H3 className="text-lg font-semibold mb-4">Quick Actions</H3>
              <div className="space-y-3">
                <Button
                  variant="primary"
                  size="sm"
                  fullWidth
                  onClick={handleCreateObservation}
                  className="flex items-center justify-center"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Observation
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  fullWidth
                  onClick={() => setActiveTab('assessments')}
                  className="flex items-center justify-center"
                >
                  <ChartBarIcon className="w-4 h-4 mr-2" />
                  View Assessments
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreateObservationModal />
    </div>
  );
};

export default ChildProfile;
